import React, { useState, useEffect } from 'react';
import ParallaxHero from '../components/ParallaxHero';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  category: string;
}

const CATEGORIES = [
  { id: 'all', label: 'Alles' },
  { id: 'aperitief', label: 'Aperitief' },
  { id: 'plus-party', label: 'Plus / Party menu' },
  { id: 'dessert', label: 'Dessert' },
  { id: 'overig', label: 'Sfeerbeelden' },
];

const GalleryPage: React.FC = () => {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetch('/images/gallery/images.json')
      .then(response => response.json())
      .then((data: GalleryImage[]) => {
        setGalleryImages(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading gallery images:', error);
        setGalleryImages([]);
        setLoading(false);
      });
  }, []);

  const filteredImages = activeCategory === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeCategory);

  return (
    <div className="bg-white">
      <ParallaxHero
        image="/images/gallery/overige/Sfeerbeelden/pensioen.jpg"
        title="Pictures"
        subtitle="- A picture is worth a thousand words -"
        backgroundPosition="0% 40%"
      />

      {/* Category Filter */}
      <section className="py-8 bg-friet-beige border-b border-friet-red/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full font-serif text-sm transition-all ${
                  activeCategory === cat.id
                    ? 'bg-friet-red text-white shadow-md'
                    : 'bg-white text-friet-oak border border-friet-red/20 hover:border-friet-red hover:text-friet-red'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <i className="fas fa-spinner fa-spin text-6xl text-friet-gold mb-4"></i>
              <p className="text-xl text-gray-600">Afbeeldingen laden...</p>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-20">
              <i className="fas fa-images text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-600 mb-2">Nog geen afbeeldingen beschikbaar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredImages.map(image => (
                <div
                  key={image.id}
                  className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300"
                  onClick={() => setLightboxImage(image.src)}
                >
                  <div className="aspect-square bg-gray-200 flex items-center justify-center">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="absolute top-4 right-4 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="fas fa-search-plus text-friet-red"></i>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-4xl hover:text-friet-gold transition"
            onClick={() => setLightboxImage(null)}
          >
            <i className="fas fa-times"></i>
          </button>
          <img
            src={lightboxImage}
            alt="Geselecteerde galerij"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
